import { UseFormReturn } from "react-hook-form";
import { Container } from "react-bootstrap";
import { useRouteContext } from "@tanstack/react-router";

import DateInput from "@/components/shared/forms/DateInput";
import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { type SLNG } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { MdLanguage } from "react-icons/md";
import { FaBriefcase } from "react-icons/fa";
import { LanguageOption, useSLNGFormLogic } from "../../useSLNGFormLogic";
import { LanguageMultiSelect } from "@/components/shared/LanguageMultiSelect";
import { validateMinDate } from "@/utils/util";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

type SLNGWizardFormProps = {
  form: UseFormReturn<SLNG, any, SLNG>;
  preschools: PreschoolSummary[];
};

export function SLNGWizardForm({ form, preschools }: SLNGWizardFormProps) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const tooltipMap = leaContext.coreData.tooltipMap.get("SLNG");
  const coreData = useCoreData();

  const {
    register,
    formState: { errors },
    getValues,
  } = form;

  const languageOptions: LanguageOption[] = (coreData.languagesList ?? []).map(
    (x: any) => ({
      Text: x.Text ?? x.text ?? "",
      Value: x.Value ?? x.value ?? "",
    })
  );

  const resetKey = "wizard-new-slng";

  const slng = getValues();

  const {
    selectedHomeLanguages,
    onHomeLanguagesChange,
    validateEffectiveStartDate,
  } = useSLNGFormLogic({
    form,
    slng,
    resetKey,
    languageOptions,
    isOpen: true,
    isEdit: false,
    currentValues: getValues(),
  });

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaBriefcase className="section-icon" />
          <h3 className="section-title">Agency Information</h3>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  readOnly
                  maxLength={50}
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EffectiveStartDate"
              labelText="Effective Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  max="9999-12-31"
                  disabled={false}
                  {...register(name, {
                    required: "Effective Start Date is required",
                    validate: (value) => {
                      const minCheck = validateMinDate(value);
                      if (minCheck !== true) return minCheck;
                      return validateEffectiveStartDate(value);
                    },
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <ReadOnlySelect
                  id={id}
                  control={form.control}
                  name={name}
                  rules={{ required: "Program Year is required" }}
                  options={coreData.programYearList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <ReadOnlySelect
                  id={id}
                  control={form.control}
                  name={name}
                  rules={{ required: "Preschool is required" }}
                  options={preschools.map((x) => ({
                    Text: x.PreschoolName,
                    Value: x.PreschoolCDSCode,
                  }))}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  readOnly
                  maxLength={50}
                  {...register(name, {
                    required: "Local Child ID (CICN) is required",
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>

      <div className="form-card-modern mb-3">
        <div className="section-header">
          <MdLanguage className="section-icon" />
          <h3 className="section-title">Language Information</h3>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="InterviewHomeLanguage1"
              labelText="Interview Home Language(s)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name, validationProps) => (
                <LanguageMultiSelect
                  id={id}
                  ariaLabel="Interview Home Language(s)"
                  ariaDescribedBy={validationProps["aria-describedby"]}
                  disabled={false}
                  required
                  invalid={!!validationProps["aria-invalid"]}
                  data={languageOptions}
                  value={selectedHomeLanguages}
                  onChange={onHomeLanguagesChange}
                  placeholder="Select up to 5"
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="InterviewMostUsedLanguage"
              labelText="Interview Most Used Language"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={false}
                  {...register(name, {
                    required: "Interview most used language is required",
                  })}
                  options={coreData.languagesList}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>
    </Container>
  );
}
